import React, { useState, useEffect, useRef } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { FileUpload } from './FileUpload';


ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, ChartDataLabels);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [userFilter, setUserFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [availableYears, setAvailableYears] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    fullName: '',
    role: 'region_manager',
    regions: []
  });
  const [createMessage, setCreateMessage] = useState('');
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUser, setEditUser] = useState({
    username: '',
    password: '',
    fullName: '',
    role: 'region_manager',
    regions: []
  });
  const [editMessage, setEditMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [confirmDeleteData, setConfirmDeleteData] = useState(false);
  const { user, logout } = useAuth();

  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, userFilter]);

  // Update availableYears when stats changes
  useEffect(() => {
    if (stats?.availableYears && Array.isArray(stats.availableYears) && stats.availableYears.length > 0) {
      console.log('useEffect - Setting availableYears from stats:', stats.availableYears);
      setAvailableYears(stats.availableYears);
    } else if (stats?.expenses && Array.isArray(stats.expenses) && stats.expenses.length > 0) {
      const years = [...new Set(stats.expenses.map(e => e.year).filter(y => y != null && !isNaN(y) && y > 1900 && y < 2100))].sort((a, b) => b - a);
      console.log('useEffect - Extracting years from expenses:', years);
      if (years.length > 0) {
        setAvailableYears(years);
      }
    }
  }, [stats]);

  // no region filter

  const fetchStats = async () => {
    try {
      setLoading(true);
      const requestId = ++latestRequestIdRef.current;

      const params = {};
      if (year !== null) params.year = year;
      if (month !== null) params.month = month;
      if (userFilter && userFilter !== 'all') params.userId = userFilter;

      const response = await api.get('/dashboard/admin-stats', {
        params,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      if (requestId === latestRequestIdRef.current) {
        console.log('Stats received:', response.data);
        console.log('Available years:', response.data?.availableYears);
        console.log('Available years type:', typeof response.data?.availableYears);
        console.log('Available years isArray:', Array.isArray(response.data?.availableYears));
        console.log('Available years length:', response.data?.availableYears?.length);
        
        // Set availableYears FIRST, before setStats
        if (response.data?.availableYears && Array.isArray(response.data.availableYears) && response.data.availableYears.length > 0) {
          console.log('Setting availableYears state from response:', response.data.availableYears);
          setAvailableYears(response.data.availableYears);
        } else if (response.data?.expenses && Array.isArray(response.data.expenses) && response.data.expenses.length > 0) {
          // Fallback: extract from expenses
          const years = [...new Set(response.data.expenses.map(e => e.year).filter(y => y != null && !isNaN(y) && y > 1900 && y < 2100))].sort((a, b) => b - a);
          console.log('Extracting years from expenses:', years);
          if (years.length > 0) {
            setAvailableYears(years);
          }
        }
        
        // Then set stats
        setStats(response.data);
      }
    } catch (error) {
      console.error('Stats olishda xatolik:', error);
    } finally {
      setLoading(false);
    }
  };



  const getPieChartData = () => {
    if (!stats?.expenseSummary || !Array.isArray(stats.expenseSummary) || stats.expenseSummary.length === 0) return null;

    const colors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'];

    return {
      labels: stats.expenseSummary.map(item => item.region),
      datasets: [{
        data: stats.expenseSummary.map(item => parseFloat(item.total_amount || 0)),
        backgroundColor: colors.slice(0, stats.expenseSummary.length),
        borderColor: colors.slice(0, stats.expenseSummary.length),
        borderWidth: 1
      }]
    };
  };

  const getBarChartData = () => {
    if (!stats) return null;

    return {
      labels: ['Plan (Umumiy Budjet 1.5%)', 'Fact (Umumiy Xarajat)'],
      datasets: [{
        label: 'Summa (so\'m)',
        data: [stats.totalBudget || 0, stats.totalExpenses || 0],
        backgroundColor: ['#28a745', '#dc3545'],
        borderColor: ['#28a745', '#dc3545'],
        borderWidth: 1
      }]
    };
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateMessage('');

    // Validate regions for Region Managers only
    if (newUser.role === 'region_manager' && 
        (!newUser.regions || newUser.regions.length === 0)) {
      setCreateMessage('❌ Region Manager uchun regionlar kerak');
      return;
    }

    try {
      const userData = {
        username: newUser.username,
        password: newUser.password,
        fullName: newUser.fullName,
        role: newUser.role,
        regions: newUser.role === 'region_manager' ? newUser.regions : null
      };

      await api.post('/users/create', userData);

      setCreateMessage('✅ Foydalanuvchi muvaffaqiyatli yaratildi!');
      setNewUser({
        username: '',
        password: '',
        fullName: '',
        role: 'region_manager',
        regions: []
      });
      setShowCreateUser(false);
      fetchStats(); // Refresh the stats to show the new user
    } catch (error) {
      console.error('User creation error:', error);
      const errorMessage = error.response?.data?.error || 'Foydalanuvchi yaratishda xatolik';
      setCreateMessage(`❌ ${errorMessage}`);
    }
  };

  const addRegion = () => {
    setNewUser({...newUser, regions: [...newUser.regions, '']});
  };

  const updateRegion = (index, value) => {
    const updatedRegions = [...newUser.regions];
    updatedRegions[index] = value;
    setNewUser({...newUser, regions: updatedRegions});
  };

  const removeRegion = (index) => {
    const updatedRegions = newUser.regions.filter((_, i) => i !== index);
    setNewUser({...newUser, regions: updatedRegions});
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditUser({
      username: userToEdit.username,
      password: '',
      fullName: userToEdit.full_name,
      role: userToEdit.role,
      regions: userToEdit.regions || []
    });
    setShowEditUser(true);
    setEditMessage('');
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setEditMessage('');

    // Validate regions for Region Managers only
    if (editUser.role === 'region_manager' && 
        (!editUser.regions || editUser.regions.length === 0)) {
      setEditMessage('❌ Region Manager uchun regionlar kerak');
      return;
    }

    try {
      const userData = {
        username: editUser.username,
        fullName: editUser.fullName,
        role: editUser.role,
        regions: editUser.role === 'region_manager' ? editUser.regions : null
      };

      // Only include password if it's not empty
      if (editUser.password.trim() !== '') {
        userData.password = editUser.password;
      }

      await api.put(`/users/${editingUser.id}`, userData);

      setEditMessage('✅ Foydalanuvchi muvaffaqiyatli yangilandi!');
      setShowEditUser(false);
      setEditingUser(null);
      setEditUser({
        username: '',
        password: '',
        fullName: '',
        role: 'region_manager',
        regions: []
      });
      fetchStats(); // Refresh the stats to show the updated user
    } catch (error) {
      console.error('User update error:', error);
      const errorMessage = error.response?.data?.error || 'Foydalanuvchini yangilashda xatolik';
      setEditMessage(`❌ ${errorMessage}`);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      setDeleteConfirm(null);
      fetchStats(); // Refresh the stats
    } catch (error) {
      console.error('User deletion error:', error);
      alert('Foydalanuvchini o\'chirishda xatolik: ' + (error.response?.data?.error || 'Noma\'lum xatolik'));
    }
  };

  const addEditRegion = () => {
    setEditUser({...editUser, regions: [...editUser.regions, '']});
  };

  const updateEditRegion = (index, value) => {
    const updatedRegions = [...editUser.regions];
    updatedRegions[index] = value;
    setEditUser({...editUser, regions: updatedRegions});
  };

  const removeEditRegion = (index) => {
    const updatedRegions = editUser.regions.filter((_, i) => i !== index);
    setEditUser({...editUser, regions: updatedRegions});
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Yuklanmoqda...</div>;
  }

  if (!user) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Foydalanuvchi ma\'lumotlari mavjud emas.</div>;
  }

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header Section */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px'
      }}>
        <div>
          <h1>Admin Dashboard</h1>
          <p>Xush kelibsiz, {user?.fullName || 'Admin'}</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            onClick={() => setShowCreateUser(true)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Foydalanuvchi yaratish
          </button>
          <button
            onClick={() => setConfirmDeleteData(true)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            title="Aktiv filtrlar bo'yicha yuklangan ma'lumotlarni o'chirish"
          >
            Ma'lumotlarni o'chirish
          </button>
          <button
            onClick={logout}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Chiqish
          </button>
        </div>
      </div>



      {/* Filters Section */}
      <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h3>Filtrlar</h3>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={year === null ? 'all' : year}
            onChange={(e) => setYear(e.target.value === 'all' ? null : parseInt(e.target.value))}
            style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="all">Barcha Yillar</option>
            {availableYears.length > 0 ? (
              availableYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))
            ) : (
              <>
                <option value={2026}>2026</option>
                <option value={2025}>2025</option>
              </>
            )}
          </select>
          <select
            value={month === null ? 'all' : month}
            onChange={(e) => setMonth(e.target.value === 'all' ? null : parseInt(e.target.value))}
            style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="all">Barcha Oylar</option>
            {[...Array(12)].map((_, i) => (
              <option key={i + 1} value={i + 1}>{i + 1}-oy</option>
            ))}
          </select>

          {/* User Filter */}
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="all">Barcha Foydalanuvchilar</option>
            {stats?.users?.filter(u => u.role !== 'admin').map(u => (
              <option key={u.id} value={u.id}>{u.full_name} ({u.username})</option>
            ))}
          </select>

          {/* Clear Filters */}
          <button
            type="button"
            onClick={() => { setYear(null); setMonth(null); setUserFilter('all'); }}
            title="Filtrlarni tozalash"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '18px',
              border: '1px solid #ddd',
              backgroundColor: '#fff',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>
      </div>

      {stats && (
        <>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            <div style={{ padding: '1.5rem', backgroundColor: '#e8f5e8', borderRadius: '8px' }}>
              <h4>Umumiy Savdo</h4>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
                {stats.totalIncome.toLocaleString()} so'm
              </p>
            </div>

            <div style={{ padding: '1.5rem', backgroundColor: '#e8f0ff', borderRadius: '8px' }}>
              <h4>Umumiy Budjet</h4>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
                {stats.totalBudget.toLocaleString()} so'm
              </p>
            </div>



            <div style={{ padding: '1.5rem', backgroundColor: '#ffe8e8', borderRadius: '8px' }}>
              <h4>Umumiy Xarajat</h4>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
                {(stats.totalExpenses || 0).toLocaleString()} so'm
              </p>
            </div>

            <div style={{ padding: '1.5rem', backgroundColor: stats.remainingBudget >= 0 ? '#e8f5e8' : '#ffe8e8', borderRadius: '8px' }}>
              <h4>Qolgan Budjet</h4>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0, color: stats.remainingBudget >= 0 ? '#28a745' : '#dc3545' }}>
                {stats.remainingBudget.toLocaleString()} so'm
              </p>
            </div>

            <div style={{ padding: '1.5rem', backgroundColor: '#e8f5e8', borderRadius: '8px' }}>
              <h4>O'tkan oy holati</h4>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0, color: stats.lastMonthRemainingBudget >= 0 ? '#28a745' : '#dc3545' }}>
                {stats.lastMonthRemainingBudget.toLocaleString()} so'm
              </p>
            </div>
          </div>

          {/* Charts Section */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3>Regionlar bo'yicha xarajatlar (Pie Chart)</h3>
              <div style={{ height: '300px' }}>
                {getPieChartData() && (
                  <Pie
                    data={getPieChartData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom'
                        },
                        datalabels: {
                          display: true,
                          color: '#000',
                          font: {
                            weight: 'bold',
                            size: 12
                          },
                          formatter: (value, context) => {
                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                            const percentage = ((value / total) * 100).toFixed(1);
                            return percentage + '%';
                          }
                        }
                      }
                    }}
                  />
                )}
              </div>
            </div>

            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3>Plan vs Fact (Bar Chart)</h3>
              <div style={{ height: '300px' }}>
                {getBarChartData() && (
                  <Bar
                    data={getBarChartData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        datalabels: {
                          display: true,
                          color: '#000',
                          font: {
                            weight: 'bold',
                            size: 12
                          },
                          formatter: (value) => {
                            return value.toLocaleString() + ' so\'m';
                          },
                          anchor: 'end',
                          align: 'top'
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true
                        }
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Tables Section */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3>Foydalanuvchilar ro'yxati</h3>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Ism</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Username</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Rol</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Regionlar</th>
                      <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Amallar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats?.users?.map(user => (
                      <tr key={user.id}>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>{user.full_name || 'Noma\'lum'}</td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>{user.username}</td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                          {user.role === 'admin' ? 'Admin' : user.role === 'region_manager' ? 'Region Manager' : 'Product Manager'}
                        </td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
                          {user.regions ? user.regions.join(', ') : 'Barcha regionlar'}
                        </td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleEditUser(user)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.875rem'
                              }}
                            >
                              Tahrirlash
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(user)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.875rem'
                              }}
                            >
                              O'chirish
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3>Regionlar bo'yicha umumiy xarajat</h3>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Region</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Umumiy Summa</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Soni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.expenseSummary?.map(summary => (
                      <tr key={summary.region}>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>{summary.region}</td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>{parseFloat(summary.total_amount || 0).toLocaleString()}</td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>{summary.count || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* All Expenses Table */}
          <div style={{ marginTop: '2rem', backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <h3>Barcha Xarajatlar</h3>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Sana</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Summa</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Xarajat turi</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Foydalanuvchi</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Comment</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Region</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.expenses?.slice(0, 20).map(expense => (
                    <tr key={expense.id}>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>{`${expense.day}/${expense.month}/${expense.year}`}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>{parseFloat(expense.amount || 0).toLocaleString()}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>{expense.expense_type || 'Noma\'lum'}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>{expense.full_name || 'Noma\'lum'}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>{expense.comment || ''}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>{expense.region || 'Noma\'lum'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* File Upload Section */}
      <div style={{ marginTop: '2rem' }}>
        <FileUpload onUploadSuccess={fetchStats} />
      </div>

      {/* Create User Modal */}
      {showCreateUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3>Foydalanuvchi yaratish</h3>
            <form onSubmit={handleCreateUser}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Username
                </label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Parol
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  To'liq ism
                </label>
                <input
                  type="text"
                  value={newUser.fullName}
                  onChange={(e) => setNewUser({...newUser, fullName: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Rol
                </label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                >
                  <option value="region_manager">Region Manager</option>
                  <option value="product_manager">Product Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {newUser.role === 'region_manager' && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Regionlar (majburiy)
                  </label>
                  {newUser.regions.map((region, index) => (
                    <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <input
                        type="text"
                        value={region}
                        onChange={(e) => updateRegion(index, e.target.value)}
                        placeholder="Region nomini kiriting"
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removeRegion(index)}
                        style={{
                          padding: '0.5rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        O'chirish
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addRegion}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Region qo'shish
                  </button>
                </div>
              )}

              {createMessage && (
                <div style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: createMessage.includes('✅') ? '#d4edda' : '#f8d7da',
                  color: createMessage.includes('✅') ? '#155724' : '#721c24',
                  borderRadius: '4px'
                }}>
                  {createMessage}
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateUser(false);
                    setCreateMessage('');
                    setNewUser({
                      username: '',
                      password: '',
                      fullName: '',
                      role: 'region_manager',
                      regions: []
                    });
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Bekor qilish
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Yaratish
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3>Foydalanuvchini tahrirlash</h3>
            <form onSubmit={handleUpdateUser}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Username
                </label>
                <input
                  type="text"
                  value={editUser.username}
                  onChange={(e) => setEditUser({...editUser, username: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Yangi parol (bo'sh qoldiring, agar o'zgartirmasangiz)
                </label>
                <input
                  type="password"
                  value={editUser.password}
                  onChange={(e) => setEditUser({...editUser, password: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  To'liq ism
                </label>
                <input
                  type="text"
                  value={editUser.fullName}
                  onChange={(e) => setEditUser({...editUser, fullName: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Rol
                </label>
                <select
                  value={editUser.role}
                  onChange={(e) => setEditUser({...editUser, role: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                >
                  <option value="region_manager">Region Manager</option>
                  <option value="product_manager">Product Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {editUser.role === 'region_manager' && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Regionlar (majburiy)
                  </label>
                  {editUser.regions.map((region, index) => (
                    <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <input
                        type="text"
                        value={region}
                        onChange={(e) => updateEditRegion(index, e.target.value)}
                        placeholder="Region nomini kiriting"
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removeEditRegion(index)}
                        style={{
                          padding: '0.5rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        O'chirish
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addEditRegion}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Region qo'shish
                  </button>
                </div>
              )}

              {editMessage && (
                <div style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: editMessage.includes('✅') ? '#d4edda' : '#f8d7da',
                  color: editMessage.includes('✅') ? '#155724' : '#721c24',
                  borderRadius: '4px'
                }}>
                  {editMessage}
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditUser(false);
                    setEditMessage('');
                    setEditingUser(null);
                    setEditUser({
                      username: '',
                      password: '',
                      fullName: '',
                      role: 'region_manager',
                      regions: []
                    });
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Bekor qilish
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Yangilash
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '400px',
            maxWidth: '90vw'
          }}>
            <h3>Foydalanuvchini o'chirish</h3>
            <p>Haqiqatan ham <strong>{deleteConfirm.full_name}</strong> foydalanuvchisini o'chirmoqchimisiz?</p>
            <p style={{ color: '#dc3545', fontSize: '0.875rem' }}>
              ⚠️ Bu amalni qaytarib bo'lmaydi!
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Bekor qilish
              </button>
              <button
                onClick={() => handleDeleteUser(deleteConfirm.id)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                O'chirish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Data Confirmation */}
      {confirmDeleteData && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', width: '480px', maxWidth: '90vw' }}>
            <h3>Ma'lumotlarni o'chirish</h3>
            <p>Haqiqatan ham quyidagi filtrlar bo'yicha yuklangan ma'lumotlarni o'chirmoqchimisiz?</p>
            <ul>
              <li>Yil: {year || 'barchasi'}</li>
              <li>Oy: {month || 'barchasi'}</li>
              <li>Foydalanuvchi: {userFilter === 'all' ? 'barchasi' : (stats?.users?.find(u => String(u.id) === String(userFilter))?.full_name || userFilter)}</li>
            </ul>
            <p style={{ color: '#dc3545', fontSize: '0.875rem' }}>⚠️ Bu amalni qaytarib bo'lmaydi!</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button
                onClick={() => setConfirmDeleteData(false)}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Bekor qilish
              </button>
              <button
                onClick={async () => {
                  try {
                    const payload = {
                      target: 'both',
                      year: year || undefined,
                      month: month || undefined,
                      userId: userFilter !== 'all' ? userFilter : undefined
                    };
                    const res = await api.delete('/upload/data', { data: payload });
                    alert(`O'chirildi: Xarajatlar ${res.data.deleted.expenses}, Savdo ${res.data.deleted.income}`);
                    setConfirmDeleteData(false);
                    fetchStats();
                  } catch (error) {
                    alert("O'chirishda xatolik: " + (error.response?.data?.error || error.message));
                  }
                }}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Ha, o'chirish
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
